/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useAppearanceSubmit.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Custom hook for submitting appearance settings.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useThemeStore } from '../../../store/theme';
import { useI18n } from '../../../hooks/useI18n';
import { getResetAppearanceValues } from '../utils';
import type { AppearanceForm, WidgetPrefs } from '../types';

interface UseAppearanceSubmitOptions {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
  isDirty: boolean;
}

/**
 * Custom hook for submitting appearance settings
 */
export function useAppearanceSubmit({ showSuccess, showError, showInfo, isDirty }: UseAppearanceSubmitOptions) {
  const { t } = useI18n();
  const { updateThemePrefs, resetThemePrefs, isLoading } = useThemeStore();

  // Handle submit - Always-Active Save Standard: always attempt to save
  const handleSubmit = async (data: AppearanceForm) => {
    try {
      // Save to database when user explicitly clicks Save
      await updateThemePrefs({
        mode: data.mode,
        darkModeVariant: data.darkModeVariant,
        lightModeVariant: data.lightModeVariant,
        primaryColor: data.primaryColor,
        secondaryColor: data.secondaryColor,
        tooltips: data.tooltips,
        notifications: data.notifications,
        // Widget settings - cast to WidgetPrefs if defined
        ...(data.widget && { widget: data.widget as WidgetPrefs }),
        ...(data.widgetDark && { widgetDark: data.widgetDark as WidgetPrefs }),
        ...(data.widgetLight && { widgetLight: data.widgetLight as WidgetPrefs }),
        // Grid settings
        ...(data.gridRowHeight && { gridRowHeight: data.gridRowHeight }),
        ...(data.gridColumns && { gridColumns: data.gridColumns }),
        // Global border radius
        ...(data.borderRadius !== undefined && { borderRadius: data.borderRadius })
      });
      showSuccess(t('appearance.success.saved') || 'Theme preferences saved successfully');
    } catch (error) {
      console.error('Failed to save theme preferences:', error);
      showError(t('appearance.errors.saveFailed') || 'Failed to save theme preferences');
    }
  };

  const handleReset = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue: (field: keyof AppearanceForm | string, value: any, options?: { shouldDirty?: boolean; shouldValidate?: boolean; shouldTouch?: boolean }) => void,
    _watchedValues: AppearanceForm
  ) => {
    try {
      await resetThemePrefs();
      showSuccess(t('appearance.success.reset') || 'Theme preferences reset to defaults');

      // Reset form to default values using fixed defaults (not current values)
      // Use the default variants for the reset values
      const resetValues = getResetAppearanceValues('white', 'very-dark-gray');

      // Use batch update with options to prevent triggering multiple re-renders
      const updateOptions = { shouldDirty: false, shouldValidate: false, shouldTouch: false };
      setValue('mode', resetValues.mode, updateOptions);
      setValue('darkModeVariant', resetValues.darkModeVariant, updateOptions);
      setValue('lightModeVariant', resetValues.lightModeVariant, updateOptions);
      setValue('primaryColor', resetValues.primaryColor, updateOptions);
      setValue('secondaryColor', resetValues.secondaryColor, updateOptions);
      setValue('tooltips', resetValues.tooltips, updateOptions);
      setValue('borderRadius', resetValues.borderRadius ?? 0.5, updateOptions);
    } catch (error) {
      showError(error.message || t('appearance.errors.resetFailed') || 'Failed to reset preferences');
    }
  };

  return {
    handleSubmit,
    handleReset,
    isLoading
  };
}

